import { alpha } from '@mui/material/styles';
import type { Color, PaletteOptions } from '@mui/material/styles';

// `ColorPartial` is only exported from MUI's internal styles/createPalette,
// which its exports map does not expose.
type ColorPartial = Partial<Color>;
import c from './tokens';

// MUI's grey ramp drives internal component math (disabled, borders). Brand
// greys are surfaced via named slots below.
const grey: ColorPartial = {
  50: '#fafafa',
  100: c.delimiterGray, // #F5F5F5
  200: c.lightGray, // #E6E6E6
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: c.gray, // #999999
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
};

const grey8 = alpha('#919EAB', 0.08);
const grey16 = alpha('#919EAB', 0.16);
const grey24 = alpha('#919EAB', 0.24);
const grey80 = alpha('#919EAB', 0.8);

const palette: PaletteOptions = {
  mode: 'light',
  grey,
  common: { black: c.blackBlue, white: c.white },

  // ROLE: filled action button. KC = Gold.
  primary: {
    main: c.gold,
    contrastText: c.white,
  },

  // ROLE: accent — links, input focus, warning
  // KC has no explicit lighter accent in the palette — MUI derives `light`.
  secondary: {
    main: c.yellow,
    contrastText: c.white,
  },

  error: {
    main: c.redError,
    light: c.redErrorBg,
    contrastText: c.white,
  },

  // ROLE: warning text. KC = Yellow (same as accent, per theme-status.md).
  warning: {
    main: c.yellow,
    contrastText: c.white,
  },

  success: {
    main: c.successGreen,
    light: c.greenBg,
    contrastText: c.white,
  },

  info: {
    main: c.gray,
    contrastText: c.white,
  },

  divider: c.delimiterGray,

  text: {
    primary: c.blackBlue,
    secondary: c.greyish,
    disabled: grey[500],
  },

  // `default` = page background (KC Background), `paper` = card/surface (white).
  // NOTE: previous config had these reversed — verify cards/dialogs at review.
  background: { default: c.white, paper: c.background },

  customColors: {
    beige: c.beige,
    inputBorder: c.lightGray,
    secondaryBtnBorder: c.paleGray,
    checkboxChecked: c.yellow,
    checkboxBorder: c.paleGold,
    headerAccent: c.green,
    errorPale: c.paleRedError,
    errorBg: c.redErrorBg,
    successBg: c.greenBg,
    highlighter: c.yellowHighlighter,
    pageBg: c.background,
    tableHover: c.tableHover,
    tabColor: c.blackBlue,
    outlinedBtnText: c.blackBlue,
    inputFocus: c.yellow,
    chipSecondaryBg: c.beige,
    chipDefaultText: c.greyish,
  },

  tonalOffset: 0.1,
  action: {
    active: grey[600],
    focus: grey24,
    hover: grey8,
    selected: grey16,
    disabled: grey80,
    disabledBackground: grey24,
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
    selectedOpacity: 0.48,
    focusOpacity: 0.48,
    activatedOpacity: 0.48,
  },
};

export default palette;
